import React, { useEffect, useState } from "react";
import { PricingSideMenuCard } from "../common/PricingSideMenuCard";
import { CommissionCardItem } from "./CommissionCardItem";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { selectAllValuesForAPI, selectFees, selectFeeValues, selectVariables, selectVariableValues } from "../../slices/pricing-values/selectors";
import { updateGlobalFeeValue, updateGlobalVariableValue } from "../../slices/pricing-values/pricingValuesSlice";
import { updateAllContractFunnelsItem } from "../../slices/pricing/pricingSlice";
import { selectGlobalGuaranteeIds, selectGlobalGuarantees } from "../../slices/pricing/selectors/guaranteeFilterSelector";
import { fetchFunnelsByVariablesOrFees } from "../../slices/pricing/thunks/fetchFunnelsByVariableOrFeesThunk";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { useParams } from "react-router-dom";

type Props = {
  updateDelay?: number; // Délai en ms avant de déclencher l'update (debounce)
}

export const CommissionCard: React.FC<Props> = ({
  updateDelay = 1000
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(NsKey.Pricing)

  // Sélecteurs pour les données de base
  const variables = useAppSelector(selectVariables);
  const fees = useAppSelector(selectFees);
  const [priceHasBeenUpdate, setPriceHasBeenUpdate] = useState(false)

  // Sélecteurs pour les valeurs courantes
  const variableValues = useAppSelector(selectVariableValues);
  const feeValues = useAppSelector(selectFeeValues);
  const guaranteeProductIds = useAppSelector(selectGlobalGuaranteeIds);
  const values = useAppSelector(selectAllValuesForAPI);
  const globalGuarantees = useAppSelector(selectGlobalGuarantees)

  const { riskId } = useParams<{ riskId: string }>()

  // Debounce pour l'auto-update des prix
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (priceHasBeenUpdate && riskId) {
        // A fee set back to its original value is no longer sent: the cards are still repriced.
        dispatch(fetchFunnelsByVariablesOrFees({
          riskId,
          guarantees: [...globalGuarantees.optional.map(g => ({
            id: g.id,
            deductible: g.deductible?.value,
            ceiling: g.ceiling?.value,
          })),
          ...globalGuarantees.mandatory.map(g => ({
            id: g.id,
            deductible: g.deductible?.value,
            ceiling: g.ceiling?.value,
          }))],
          fees: values.fees,
          variables: values.variables
        }))
      }
      setPriceHasBeenUpdate(false)
    }, updateDelay);

    return () => clearTimeout(timeoutId);
  }, [updateDelay, dispatch, values, globalGuarantees, guaranteeProductIds, priceHasBeenUpdate, riskId]);

  // Handlers pour les changements de valeurs
  const handleVariableChange = (variableId: string) => {
    return (setValue: React.SetStateAction<number>) => {
      const currentValue = variableValues[variableId] || 0;
      const newValue = typeof setValue === 'function'
        ? setValue(currentValue)
        : setValue;

      dispatch(updateGlobalVariableValue({ id: variableId, value: newValue }));
      dispatch(updateAllContractFunnelsItem({ itemId: variableId, type: 'variable', newValue }));
      setPriceHasBeenUpdate(true)
    };
  };

  const handleFeeChange = (feeCode: string) => {
    return (setValue: React.SetStateAction<number>) => {
      const currentValue = feeValues[feeCode] || 0;
      const newValue = typeof setValue === 'function'
        ? setValue(currentValue)
        : setValue;
      dispatch(updateGlobalFeeValue({ itemId: feeCode, value: newValue }));
      dispatch(updateAllContractFunnelsItem({ itemId: feeCode, type: 'fee', newValue }));
      setPriceHasBeenUpdate(true)
    };
  };

  return (
    <PricingSideMenuCard
      title={t("PRICING.SIDE_MENU.FEES_AND_COMMISSION")}
      icon="filter-vertical"
    >
      <div className="flex flex-row justify-between xl:justify-normal xl:flex-col gap-4">
        {/* Rendu des variables */}
        {variables && variables.map((variable) => (
          <CommissionCardItem
            disabledUnit
            key={`variable-${variable.variableRefId}`}
            title={variable.label}
            subtitle={variable.parameters && variable.parameters.unit ? `en ${variable.parameters.unit}` : undefined}
            value={variableValues[variable.variableRefId] ?? variable.value}
            onChange={handleVariableChange(variable.variableRefId)}
            parameters={variable.parameters}
          />
        ))}

        {/* Rendu des fees */}
        {/* Hidden fees stay in the price and in the values sent back; only their entry is not shown. */}
        {fees && fees.filter((fee) => fee.displayedOnPricing !== false).map((fee) => (
          <CommissionCardItem
            disabledUnit
            key={`fee-${fee.code}`}
            title={fee.label}
            subtitle={fee.parameters && fee.parameters.unit ? `en ${fee.parameters.unit}` : undefined}
            value={feeValues[fee.code] ?? fee.value}
            onChange={handleFeeChange(fee.code)}
            parameters={fee.parameters}
          />
        ))}
      </div>
    </PricingSideMenuCard>
  );
};
